import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BsArrowUpRight, BsBoxSeam, BsClipboard2Check, BsJournalBookmark, BsPeople, BsPersonBadge, BsPlayBtn, BsReceipt } from 'react-icons/bs';
import { DContext } from '../../../context/DataContext';

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { BeURL } = useContext(DContext);
  const [count, setCount] = useState({
    packages: null,
    bookings: null,
    users: null,
    videoAd: null
  });

  useEffect(() => {
    if (!BeURL) {
      return;
    }

    fetch(`${BeURL}/fetch-packages-count`, {
      credentials: 'include'
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setCount((prev) => ({ ...prev, packages: data.count }));
        }
      })
      .catch((err) => {
        console.log('Error in fetching packages count:', err);
      });

    fetch(`${BeURL}/fetch-bookings-count`, {
      credentials: 'include'
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setCount((prev) => ({ ...prev, bookings: data.count }));
        }
      })
      .catch((err) => {
        console.log('Error in fetching bookings count:', err);
      });

    fetch(`${BeURL}/fetch-users-count`, {
      credentials: 'include'
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setCount((prev) => ({ ...prev, users: data.count }));
        }
      })
      .catch((err) => {
        console.log('Error in fetching users count:', err);
      });

    fetch(`${BeURL}/fetch-ad-video`, {
      credentials: 'include'
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setCount((prev) => ({ ...prev, videoAd: 1 }));
        }
      })
      .catch((err) => {
        console.log('Error in fetching ad video:', err);
      });
  }, [BeURL]);

  const cards = [
    {
      icon: BsBoxSeam,
      title: t('dashboard.packages'),
      count: count.packages ?? '..',
      to: '/admin/manage-packages',
      color: 'from-rose-500 to-orange-400',
      description: t('dashboard.cards.packagesDescription')
    },
    {
      icon: BsJournalBookmark,
      title: t('dashboard.bookings'),
      count: count.bookings ?? '..',
      to: '/admin/manage-bookings',
      color: 'from-sky-600 to-cyan-500',
      description: t('dashboard.cards.bookingsDescription')
    },
    {
      icon: BsPeople,
      title: t('dashboard.users'),
      count: count.users ?? '..',
      to: '/admin/users',
      color: 'from-amber-500 to-yellow-400',
      description: t('dashboard.cards.usersDescription')
    },
    {
      icon: BsPlayBtn,
      title: t('dashboard.videoAd'),
      count: count.videoAd ?? '0',
      to: '/admin/video-ad',
      color: 'from-indigo-600 to-violet-500',
      description: t('dashboard.cards.videoAdDescription')
    }
  ];

  const quickStats = [
    {
      title: t('dashboard.metrics.catalogReadiness'),
      value: count.packages === null
        ? t('dashboard.metrics.checking')
        : count.packages > 0
          ? t('dashboard.metrics.ready')
          : t('dashboard.metrics.needsPackages'),
      icon: BsClipboard2Check,
      tone: count.packages > 0 ? 'text-emerald-700' : 'text-amber-700'
    },
    {
      title: t('dashboard.metrics.bookingFlow'),
      value: count.bookings === null
        ? t('dashboard.metrics.checking')
        : count.bookings > 0
          ? t('dashboard.metrics.active')
          : t('dashboard.metrics.noBookingsYet'),
      icon: BsReceipt,
      tone: count.bookings > 0 ? 'text-emerald-700' : 'text-slate-700'
    },
    {
      title: t('dashboard.metrics.userBase'),
      value: count.users === null ? t('dashboard.metrics.checking') : t('dashboard.metrics.accounts', { count: count.users }),
      icon: BsPersonBadge,
      tone: 'text-slate-800'
    }
  ];

  return (
    <div className="space-y-5">
      <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_20px_45px_rgba(15,23,42,0.08)]">
        <div className="pointer-events-none absolute -right-16 -top-14 h-48 w-48 rounded-full bg-indigo-100/70 blur-2xl"></div>
        <div className="pointer-events-none absolute -bottom-20 left-6 h-52 w-52 rounded-full bg-sky-100/60 blur-2xl"></div>

        <div className="relative p-5 sm:p-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#1739bd]">{t('dashboard.admin')}</p>
            <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
              {t('dashboard.controlCenterTitle')}
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
              {t('dashboard.controlCenterDescription')}
            </p>

            <div className="mt-5 grid gap-3 md:grid-cols-3">
              {quickStats.map((metric) => (
                <div key={metric.title} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-[#1739bd]">
                    <metric.icon className="text-base" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{metric.title}</p>
                    <p className={`mt-1 text-sm font-semibold ${metric.tone}`}>{metric.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-bold tracking-tight text-slate-900">{t('dashboard.quickActions')}</h3>
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{t('dashboard.adminModules')}</span>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {cards.map((card) => (
            <Link
              key={card.title}
              to={card.to}
              className="group rounded-3xl border border-slate-200 bg-white p-4 no-underline shadow-[0_14px_30px_rgba(15,23,42,0.06)] transition duration-200 hover:-translate-y-1 hover:shadow-[0_20px_35px_rgba(15,23,42,0.1)]"
            >
              <div className="flex items-center justify-between gap-3">
                <span className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br ${card.color} text-lg text-white shadow-lg`}>
                  <card.icon className="text-base" />
                </span>
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition group-hover:text-[#1739bd]">
                  <BsArrowUpRight />
                </span>
              </div>

              <h5 className="mt-4 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{card.title}</h5>
              <p className="mt-1 text-4xl font-extrabold tracking-tight text-slate-900">{card.count}</p>
              <p className="mt-2 text-sm text-slate-600">{card.description}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
